import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { List, OrderedMap, OrderedSet, Set } from 'immutable';
import { EventHttpService } from './event.http.service';
import { OnyxSummaryEvent } from '../models/event.summary';
import { Page } from '../../shared/models/page';
import { ErrorHandlerService } from '../../shared/utils/error-handler.service';
import { EventSearchParametersStore } from './event-search-parameters.store';
import { EventSearchParameters, EventSearchParamsDesc } from '../models/event-search-params';
import { saveAs } from 'file-saver';
import { DateUtil } from '../../shared/utils/date.util';
import { LogService } from '../../shared/log/log.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Constants } from '../../shared/constants';
import { EventFilterType } from '../models/event-filter-type-enum';
import { EventSearchFilter } from '../models/event-filter';
import { EventFilterUtilsService } from './event-filter-util-service';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';

@Injectable()
export class EventListStore {

  private eventPage: BehaviorSubject<Page<OnyxSummaryEvent>> = new BehaviorSubject(Page.empty<OnyxSummaryEvent>());

  private bulkActionPageSubject: BehaviorSubject<Page<OnyxSummaryEvent>> = new BehaviorSubject(Page.empty<OnyxSummaryEvent>());

  constructor(private eventHttpService: EventHttpService,
              private errorHandlerService: ErrorHandlerService,
              private searchParametersStore: EventSearchParametersStore,
              private logService: LogService,
              private flashMessagesService: FlashMessagesService,
              private eventFilterUtils: EventFilterUtilsService,
              private environment: EnvironmentConfiguration) {
  }

  get events(): Observable<Page<OnyxSummaryEvent>> {
    return this.eventPage.asObservable();
  }

  get eventsValue(): Page<OnyxSummaryEvent> {
    return this.eventPage.value;
  }

  get bulkActionPage(): Observable<Page<OnyxSummaryEvent>> {
    return this.bulkActionPageSubject.asObservable();
  }

  get bulkActionPageValue(): Page<OnyxSummaryEvent> {
    return this.bulkActionPageSubject.value;
  }

  get totalEventCount(): Observable<number> {
    return this.eventPage.asObservable().map(page => page.totalElements);
  }

  get sortOptionsApplied(): Observable<Object> {
    return this.eventPage.asObservable().map(page => page.sort);
  }

  loadBulkActionEvents(page: number, itemsPerPage: number, filters?: OrderedSet<EventSearchFilter>): Observable<Page<OnyxSummaryEvent>> {

    const pageNumber = (page || page === 0) ? page : Constants.SEARCH_DEFAULT_PAGE;

    // filter set
    let filtersSet: OrderedSet<EventSearchFilter> = filters ? OrderedSet(filters) : OrderedSet([]);
    this.searchParametersStore.eventSearchParametersSnapshot.filters.forEach((filter: EventSearchFilter) => {
      filtersSet = filtersSet.add(filter);
    });

    // search params
    const searchParameters = new EventSearchParameters({
      searchText: this.searchParametersStore.eventSearchParametersSnapshot.searchText,
      filters: filtersSet,
      maxEvents: this.environment.events.maxEvents
    });

    return this.eventHttpService.search(searchParameters, pageNumber, itemsPerPage)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.SEARCH_EVENTS',
          `Error retrieving events: ${error}`);
        return Observable.throw(error);
      })
      .map((res: Page<OnyxSummaryEvent>) => {
        this.bulkActionPageSubject.next(res);
        this.logService.debug(`loadEvents result: ${res}`);
        return res;
      });
  }

  loadEvents(page: number, itemsPerPage: number, isSpinner = true): Observable<Page<OnyxSummaryEvent>> {
    const pageNumber = (page || page === 0) ? page : Constants.SEARCH_DEFAULT_PAGE;

    this.logService.debug(
      `searchParameters: ${this.searchParametersStore.eventSearchParametersSnapshot},
       page: ${pageNumber},
       size: ${itemsPerPage}`
    );
    return this.eventHttpService.search(this.searchParametersStore.eventSearchParametersSnapshot, pageNumber, itemsPerPage, isSpinner)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.SEARCH_EVENTS',
          `Error retrieving events: ${error}`);
        return Observable.throw(error);
      })
      .map((res: Page<OnyxSummaryEvent>) => {
        this.eventPage.next(res);
        this.logService.debug(`loadEvents result: ${res}`);
        return res;
      });
  }

  loadRecentlyEvents(eventParameters: EventSearchParameters): Observable<List<OnyxSummaryEvent>> {
    return this.eventHttpService.getRecentlyEvents(eventParameters)
      .catch(this.errorHandlerService.handleObservable('Error retrieving events', 'Could not retrieve events!'))
      .map(page => {
        return page.content;
    });
  }

  exportEventExcel(searchParameters: EventSearchParamsDesc): Observable<any> {
    return this.eventHttpService.exportEventExcel(searchParameters).map( data => {
      saveAs(data, 'event_list_' + DateUtil.formatDate(new Date(), 'YYYYMMDD') + '.xlsx');
    });
  }

  /*
  setStayOnPage(page?: number, size?: number) {
    this.pageNumber = page;
    this.itemsPerPage = size;
  }
  */

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/event-list.store.ts